import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Flex, Input, Typography } from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import {
  useAdminStepUpRequest,
  useAdminStepUpVerify,
} from '../../../libs/ohat-api';

// 管理画面は i18n 対象外のため、文言は日本語固定

/** 再送信できるまでの秒数 */
const RESEND_INTERVAL_SECONDS = 60;

/** API エラーレスポンスのうち、この画面で使う項目 */
interface StepUpErrorBody {
  code?: string;
  remainingAttempts?: number;
  retryAfterSeconds?: number;
}

const getErrorBody = (error: unknown): StepUpErrorBody =>
  ((error as { response?: { data?: StepUpErrorBody } })?.response?.data ??
    {}) as StepUpErrorBody;

/** エラーコードを画面表示用の文言に変換する */
const toErrorMessage = (body: StepUpErrorBody): string => {
  switch (body.code) {
    case 'INVALID_CODE':
      return body.remainingAttempts && body.remainingAttempts > 0
        ? `確認コードが正しくありません（残り ${body.remainingAttempts} 回）`
        : '確認コードが正しくありません。確認コードを再送信してください';
    case 'CODE_EXPIRED':
      return '確認コードの有効期限が切れています。確認コードを再送信してください';
    case 'TOO_MANY_ATTEMPTS':
      return '確認コードの入力回数が上限に達しました。確認コードを再送信してください';
    case 'RESEND_TOO_SOON':
      return '確認コードの再送信は少し時間をおいてから行ってください';
    default:
      return 'エラーが発生しました。時間をおいて再度お試しください';
  }
};

/**
 * 管理画面のステップアップ認証（メール確認コードの入力）画面
 */
export function AdminStepUpVerifyPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const requestMutation = useAdminStepUpRequest();
  const verifyMutation = useAdminStepUpVerify();

  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [cooldown, setCooldown] = useState(0);
  // StrictMode の二重実行で確認コードを 2 回送らないためのフラグ
  const requestedRef = useRef(false);

  const sendCode = async () => {
    setErrorMessage(undefined);
    try {
      const result = await requestMutation.mutateAsync();
      setSentTo(result.sentTo);
      setCode('');
      setCooldown(RESEND_INTERVAL_SECONDS);
    } catch (e) {
      const body = getErrorBody(e);
      if (body.code === 'RESEND_TOO_SOON' && body.retryAfterSeconds) {
        setCooldown(body.retryAfterSeconds);
      }
      setErrorMessage(toErrorMessage(body));
    }
  };

  // マウント時に 1 回だけ確認コードを送信する
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 再送信までのカウントダウン
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((sec) => sec - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const onVerify = async () => {
    setErrorMessage(undefined);
    try {
      await verifyMutation.mutateAsync({ data: { code } });
      // adminStepUpRequired が更新された最新のアカウント情報を取り込んでから管理画面へ戻る
      await auth.fetchAccount(auth.username);
      navigate('/admin', { replace: true });
    } catch (e) {
      setErrorMessage(toErrorMessage(getErrorBody(e)));
    }
  };

  const isCodeValid = /^\d{6}$/.test(code);

  return (
    <Flex
      justify="center"
      align="center"
      style={{ minHeight: '100vh', padding: 16 }}
    >
      <Card title="管理画面の確認" style={{ width: '100%', maxWidth: 420 }}>
        <Flex vertical gap={16}>
          <Typography.Text>
            {sentTo
              ? `確認コードを ${sentTo} に送信しました（10 分有効）。`
              : '確認コードを送信しています…'}
          </Typography.Text>
          {errorMessage && (
            <Alert type="error" showIcon message={errorMessage} />
          )}
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            onPressEnter={() => isCodeValid && onVerify()}
            placeholder="6 桁の確認コード"
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
            size="large"
          />
          <Button
            type="primary"
            size="large"
            block
            disabled={!isCodeValid}
            loading={verifyMutation.isPending}
            onClick={onVerify}
          >
            確認
          </Button>
          <Button
            block
            disabled={cooldown > 0}
            loading={requestMutation.isPending}
            onClick={sendCode}
          >
            {cooldown > 0 ? `再送信（${cooldown} 秒後に可能）` : '再送信'}
          </Button>
          <Link to="/" style={{ textAlign: 'center' }}>
            管理画面を使わずに戻る
          </Link>
        </Flex>
      </Card>
    </Flex>
  );
}
